var _dec, _class, _descriptor;
function _initializerDefineProperty(target, property, descriptor, context) { if (!descriptor) return; Object.defineProperty(target, property, { enumerable: descriptor.enumerable, configurable: descriptor.configurable, writable: descriptor.writable, value: descriptor.initializer ? descriptor.initializer.call(context) : void 0 }); }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }
function _initializerWarningHelper(descriptor, context) { throw new Error('Decorating class property failed. Please ensure that ' + 'transform-class-properties is enabled and runs after the decorators transform.'); }
import Service, { inject as service } from '@ember/service';
import { jsonPost } from '@square/dashboard-utilities/utils/xhr';
import { isPresent } from '@ember/utils';
import { camelize, underscore } from '@square/dashboard-utilities/utils/keys';
import { A } from '@ember/array';
import DeviceProfileError, { DeviceProfileMessage } from '@square/dashboard-data/models/device-profiles-v2/device-profile-error';
import { Status } from '@square/dashboard-protos/models/squareup/devicesettings/profiles/v2/message/status';
import { captureException } from '@sentry/browser';
import { LinkDeviceProfilesV2Request, LinkDeviceProfilesV2Response } from '@square/dashboard-protos/models/squareup/devicesettings/profiles/v2/message/link_device_profiles';
import { GetDeviceProfileLinksV2Request, GetDeviceProfileLinksV2Response } from '@square/dashboard-protos/models/squareup/devicesettings/profiles/v2/message/get_device_profile_links';
import { Pagination } from '@square/dashboard-protos/models/squareup/devicesettings/profiles/v2/message/pagination';
const DEVICE_PROFILE_V2_URL_PREFIX = '/2.0/device_profiles';
const DEVICE_PROFILE_LINK_CREATE_LINK_URL = `${DEVICE_PROFILE_V2_URL_PREFIX}/link`;
const DEVICE_PROFILE_LINK_GET_LINKS_URL = `${DEVICE_PROFILE_V2_URL_PREFIX}/get_links`;
const SUCCESSFUL_STATUSES = new Set([Status.SUCCESS, Status.NO_LINKS_FOUND // the default response status when no links are created yet
]);
const MAX_DEVICE_LINKS_RESULTS = 100;
const MAX_DEVICE_LINKS_REQUEST = 100;
const MAX_PARALLEL_REQUESTS = 5;
let DeviceProfileLink = (_dec = service('device-profiles-v2/cdp-events'), (_class = class DeviceProfileLink extends Service {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "cdpEventsService", _descriptor, this);
  }
  createBatchLinkDevicesToProfile(batchLinkDevicesToProfile) {
    return this._createOrUpdateDeviceProfileLink([], batchLinkDevicesToProfile);
  }
  createOrUpdateDeviceProfileLink(deviceLinks) {
    const batchQueue = this._chunkArray(deviceLinks, MAX_DEVICE_LINKS_REQUEST);
    const _createOrUpdateDeviceProfileLink = this._createOrUpdateDeviceProfileLink.bind(this);
    async function _processRequests(queue, count) {
      const batches = [];
      while (batches.length < count && queue.length > 0) {
        batches.push(queue.pop());
      }
      const inFlightRequests = batches.map(batch => {
        return _createOrUpdateDeviceProfileLink(batch);
      });
      const responses = await Promise.all(inFlightRequests);
      const linkedProfileLinks = responses.reduce((acc, response) => {
        const {
          linkedProfileLinks
        } = response;
        return [...acc, ...linkedProfileLinks];
      }, []);
      return linkedProfileLinks;
    }
    const processRequests = _processRequests.bind(this);
    async function _processQueue(queue, acc) {
      const pairedLinks = await processRequests(queue, MAX_PARALLEL_REQUESTS);
      const totalPairedLinks = [...acc, ...pairedLinks];
      if (queue.length > 0) {
        return _processQueue(queue, totalPairedLinks);
      }
      return LinkDeviceProfilesV2Response.create({
        linkedProfileLinks: totalPairedLinks,
        unlinkedProfileLinks: []
      });
    }
    const processQueue = _processQueue.bind(this);
    return processQueue(batchQueue, []);
  }
  _createOrUpdateDeviceProfileLink(deviceLinks, batchLinkDevicesToProfile) {
    const linkDeviceProfilesV2Request = LinkDeviceProfilesV2Request.create({
      deviceProfileLinks: deviceLinks,
      batchLink: batchLinkDevicesToProfile
    });
    const json = underscore(linkDeviceProfilesV2Request.toJSON());
    return jsonPost(DEVICE_PROFILE_LINK_CREATE_LINK_URL, json).then(resp => {
      const proto = LinkDeviceProfilesV2Response.fromObject(camelize(resp));
      if (!this.isSuccess(proto.status)) {
        this.cdpEventsService.maybeTrackProfileFetchUnexpectedStatus({
          status: Status[proto.status]
        });
        throw new DeviceProfileMessage(`Error creating/updating device profile links -- Status: ${proto.status}`, Status[proto.status]);
      }
      return proto;
    });
  }
  async getDeviceProfileLinks(deviceProfileType = undefined, cursor = undefined, objects = []) {
    const {
      links,
      nextCursor
    } = await this._getDeviceProfileLinks(deviceProfileType, cursor, MAX_DEVICE_LINKS_RESULTS);
    const newFetchedLinks = A([...objects, ...links]);
    return isPresent(nextCursor) && nextCursor !== 0 ? this.getDeviceProfileLinks(deviceProfileType, nextCursor, newFetchedLinks) : newFetchedLinks;
  }
  _getDeviceProfileLinks(deviceProfileType, cursor, maxResults) {
    const getDeviceProfileLinksRequest = new GetDeviceProfileLinksV2Request.Builder().deviceProfileType(deviceProfileType).pagination(Pagination.create({
      cursor,
      maxResults
    })).build();
    const json = underscore(getDeviceProfileLinksRequest.toJSON());
    return jsonPost(DEVICE_PROFILE_LINK_GET_LINKS_URL, json).then(resp => {
      const proto = GetDeviceProfileLinksV2Response.fromObject(camelize(resp));
      if (!this.isSuccess(proto.status)) {
        this.cdpEventsService.maybeTrackProfileFetchUnexpectedStatus({
          status: Status[proto.status]
        });
        throw new DeviceProfileMessage(`Error fetching device profile links -- Status: ${proto.status}`, Status[proto.status]);
      }
      return proto;
    }).catch(err => {
      captureException(err);
      throw new DeviceProfileError('Error fetching device profile links', err);
    });
  }
  isSuccess(status) {
    return SUCCESSFUL_STATUSES.has(status);
  }
  _chunkArray(array, chunkSize) {
    const arrays = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      arrays.push(array.slice(i, i + chunkSize));
    }
    return arrays;
  }
}, (_descriptor = _applyDecoratedDescriptor(_class.prototype, "cdpEventsService", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
})), _class));
export { DeviceProfileLink as default };